import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useParams } from 'react-router-dom';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const ClinicalActivity = ({ onBack }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ year: sharedMonthYear.year });
    const [data, setData] = useState({
        kpis: [],
        activityData: [],
        appointmentTypes: [],
        occupancyData: []
    });

    useEffect(() => {
        const fetchClinicalActivity = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/clinical?year=${filters.year}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching clinical activity:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchClinicalActivity();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading clinical activity overview...</div>;
    }

    const filterMonthlyChartData = (chartData, year) => {
        const now = new Date();
        if (year < now.getFullYear()) return chartData;
        return chartData.slice(0, now.getMonth() + 1);
    };

    const activityData = filterMonthlyChartData(data.activityData, filters.year);
    const hasActivityData = activityData.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader
                title="Clinical Activity Overview"
                subtitle={`Year ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="year"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                {/* Left column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
                    {/* Clinical activity summary */}
                    <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                        <div className="clinic-dashboard-section-title">Clinical activity summary</div>
                        <div className="clinic-dashboard-kpi-row">
                            {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                        </div>
                    </div>

                    {/* Activity over time */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, animationDelay: '80ms' }}>
                        <div className="clinic-dashboard-section-title">Activity over time</div>
                        {hasActivityData ? (
                            <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={activityData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis
                                            dataKey="name"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#9ca3af', fontSize: 12 }}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#9ca3af', fontSize: 12 }}
                                        />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        />
                                        <Legend
                                            verticalAlign="bottom"
                                            height={36}
                                            iconType="circle"
                                            iconSize={8}
                                            wrapperStyle={{ fontSize: '13px', color: '#4b5563' }}
                                        />
                                        <Line animationBegin={600} type="monotone" dataKey="revenue" stroke="#0f766e" strokeWidth={2} dot={{ r: 4, fill: '#0f766e' }} activeDot={{ r: 6 }} />
                                        <Line animationBegin={600} type="monotone" dataKey="visit" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: '#f97316' }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <EmptyState message="No activity data for this year" />
                        )}
                    </div>
                </div>

                {/* Right column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
                    {/* Appointment by type */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, animationDelay: '160ms' }}>
                        <div className="clinic-dashboard-section-title">Appointment by type</div>
                        <div className="clinic-dashboard-catbar-list medium" style={{ justifyContent: 'center' }}>
                            {data.appointmentTypes.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    </div>

                    {/* Clinic Occupancy */}
                    <div className="clinic-dashboard-section-card" style={{ animationDelay: '240ms' }}>
                        <div className="clinic-dashboard-section-title">Clinic Occupancy</div>
                        <div className="clinic-dashboard-catbar-list large" style={{ justifyContent: 'center' }}>
                            {data.occupancyData.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClinicalActivity;
